/**
 * @copyright Copyright 2024-2025 Epic Systems Corporation
 * @file Attestation questionnaire.
 * @module Epic.AppOrchard.Core.Questionnaire.Attestation
 */
import {
	Box,
	Button,
	ButtonGroup,
	Divider,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	useToast,
} from "@chakra-ui/react";
import { useAsync } from "@epic/react-async-hook";
import { config } from "ao/appConfig";
import { SingleSaveQuestionnaire } from "ao/components/Questionnaire/SingleSaveQuestionnaire";
import useQuestionnaire from "ao/components/Questionnaire/UseQuestionnaire";
import { IAttestationModel } from "ao/data";
import { IQuestionnaireCategoryForEditing } from "ao/types";
import { isNullOrWhitespace } from "ao/utils/helpers";
import { isInFlight } from "ao/utils/useAsyncHelpers";
import React, { memo, useEffect, useMemo, useState } from "react";
import { BiSave } from "react-icons/bi";
import { ErrorMessage } from "../Core";
import { MainPageSpinner } from "../Core/MainPageSpinner";
import { buildAcknowledgementText, saveAttestationAcknowledgement } from "./data/data";

interface IProps {
	attestation: IAttestationModel;
	appId: number;
	isVisible: boolean;
	headerButtons?: React.ReactNode;
	footerButtons?: React.ReactNode;
	cancelButton?: boolean;
	onComplete: (acknowledgementText: string, updatedCategories: IQuestionnaireCategoryForEditing[]) => void;
	onCancel?: () => void;
	testHooksOnTextBuild?: boolean;
	resetOnOpen?: boolean;
}

export const AttestationQuestionnaireModal: React.FunctionComponent<IProps> = memo((props: IProps) => {
	const {
		attestation,
		appId,
		isVisible,
		headerButtons,
		footerButtons,
		cancelButton,
		onComplete,
		onCancel,
		testHooksOnTextBuild,
		resetOnOpen,
	} = props;

	const toast = useToast();

	const questionnaireVersion = attestation?.version ?? -1;

	const [showAcknowledgement, setShowAcknowledgement] = useState<boolean>(false);
	const [acknowledgementText, setAcknowledgementText] = useState<string>("");
	const [hookImpactText, setHookImpactText] = useState<string>("");
	const [isQuestionnaireSaved, setIsQuestionnaireSaved] = useState<boolean>(false);
	const [isAcknowledgementSaved, setIsAcknowledgementSaved] = useState<boolean>(false);
	const [isComplete, setIsComplete] = useState<boolean>(false);
	const [errorBuildingAcknowledgementText, setErrorBuildingAcknowledgementText] = useState<boolean>(false);

	const categories = useMemo(() => {
		return attestation?.categories ?? [];
	}, [attestation?.categories]);

	const {
		saveHandler: questionnaireSaveHandler,
		buildSaveModel,
		answerChangeHandler,
		savingState,
		categories: stateCategories,
		allQuestionsAnswered,
		resetQuestionnaire,
	} = useQuestionnaire(
		categories,
		questionnaireVersion,
		false,
		appId,
		false,
		undefined,
		() => {
			setIsQuestionnaireSaved(true);
		},
		() => {
			setIsQuestionnaireSaved(true);
		},
	);

	const [savingAttestationState, saveAttestation] = useAsync(saveAttestationAcknowledgement, {
		executeImmediately: false,
		displayName: "saveAttestationAcknowledgement",
		onSuccess: (response) => {
			if (response.success) {
				setIsAcknowledgementSaved(true);
			} else {
				toast({
					title: "Error",
					description:
						"Something went wrong when saving your responses. Refresh the page and try again.",
					status: "error",
					duration: config.ToastDuration,
					isClosable: true,
				});
			}
		},
		onFailure: () => {
			toast({
				title: "Error",
				description:
					"Something went wrong when saving your responses. Refresh the page and try again.",
				status: "error",
				duration: config.ToastDuration,
				isClosable: true,
			});
		},
	});

	const [buildAcknowledgementTextState, buildAcknowledgementTextHandler] = useAsync(
		buildAcknowledgementText,
		{
			executeImmediately: false,
			displayName: "buildAcknowledgementText",
			onSuccess: (response) => {
				if (response.success) {
					setAcknowledgementText(response.text);
					setHookImpactText(response.testResponse.hookImpactText);
				} else {
					setErrorBuildingAcknowledgementText(true);
				}
			},
			onFailure: () => {
				setErrorBuildingAcknowledgementText(true);
			},
		},
	);

	useEffect(() => {
		if (isAcknowledgementSaved && !isQuestionnaireSaved) {
			questionnaireSaveHandler();
		}
	}, [isAcknowledgementSaved, isQuestionnaireSaved, questionnaireSaveHandler]);

	useEffect(() => {
		if (isQuestionnaireSaved && isAcknowledgementSaved && !isComplete) {
			setIsComplete(true);

			const updatedCategories = stateCategories.map((internalCategory) => ({
				...internalCategory,
				questions: internalCategory.questions.map((question) => ({
					...question,
					options: question.options.map((option) => ({ ...option })),
				})),
			}));
			onComplete(acknowledgementText, updatedCategories);
		}
	}, [
		acknowledgementText,
		isAcknowledgementSaved,
		isComplete,
		isQuestionnaireSaved,
		onComplete,
		stateCategories,
	]);

	useEffect(() => {
		if (isVisible && resetOnOpen) {
			setShowAcknowledgement(false);
			setAcknowledgementText("");
			setHookImpactText("");
			setIsQuestionnaireSaved(false);
			setIsAcknowledgementSaved(false);
			setIsComplete(false);
			setErrorBuildingAcknowledgementText(false);
			resetQuestionnaire();
		}
	}, [isVisible, resetOnOpen, resetQuestionnaire]);

	//#region saving data
	const isSaving = isInFlight(savingState) || isInFlight(savingAttestationState);
	const isBuildingAcknowledgementText = isInFlight(buildAcknowledgementTextState);
	//#endregion

	return (
		<Modal
			isOpen={isVisible}
			onClose={() => {
				if (onCancel) onCancel();
			}}
			size="3xl"
		>
			<ModalOverlay />
			<ModalContent>
				<>
					<ModalHeader>{(attestation && attestation.titleText) || "Questionnaire"}</ModalHeader>
					{cancelButton && onCancel && <ModalCloseButton onClick={onCancel} />}
					<ModalBody>
						{headerButtons && (
							<Box pos="absolute" top="4" right="3">
								{headerButtons}
							</Box>
						)}
						{attestation && attestation.headerText && (
							<Text dangerouslySetInnerHTML={{ __html: attestation.headerText }} />
						)}
						<Divider mt={attestation && attestation.headerText ? "20px" : "0"} mx="0" mb="15px" />
						<Box
							maxH={{
								base: showAcknowledgement ? "40vh" : "50vh",
								md: showAcknowledgement ? "55vh" : "65vh",
							}}
							overflowY="auto"
							style={{ scrollbarGutter: "stable" }}
							px="3px"
						>
							{showAcknowledgement ? (
								acknowledgementText ? (
									<>
										<Text
											whiteSpace="pre-line"
											dangerouslySetInnerHTML={{ __html: acknowledgementText }}
										/>
										{hookImpactText && (
											<>
												<Divider m="20px 0 15px" />
												<Text dangerouslySetInnerHTML={{ __html: hookImpactText }} />
											</>
										)}
									</>
								) : (
									<>
										{isBuildingAcknowledgementText && (
											<Box h="150px">
												<MainPageSpinner />
											</Box>
										)}
										{errorBuildingAcknowledgementText && (
											<ErrorMessage
												message="Something went wrong when summarizing your answers. Refresh the page and try again."
												isNotForm
											/>
										)}
									</>
								)
							) : (
								<SingleSaveQuestionnaire
									categories={stateCategories}
									answerChangeHandler={answerChangeHandler}
								/>
							)}
						</Box>
						{showAcknowledgement && !isNullOrWhitespace(attestation.footerText) && (
							<>
								<Divider mt="20px" mb="15px" />
								<Text>{attestation.footerText}</Text>
							</>
						)}
					</ModalBody>
					<ModalFooter>
						<ButtonGroup justifyContent="end" flexFlow="wrap" gridRowGap={1}>
							{showAcknowledgement ? (
								<>
									<Button
										colorScheme="blue"
										type="button"
										disabled={
											isBuildingAcknowledgementText || errorBuildingAcknowledgementText
										}
										onClick={() => {
											saveAttestation({
												appId,
												questionnaireVersionId: questionnaireVersion,
												notAccurate: false,
											});
										}}
										title="Save your answers"
										leftIcon={<BiSave />}
									>
										Confirm
									</Button>
									<Button
										type="button"
										onClick={() => {
											setShowAcknowledgement(false);
										}}
									>
										I Need to Make a Change
									</Button>
								</>
							) : (
								<Button
									colorScheme="blue"
									isLoading={isSaving}
									loadingText="Saving"
									disabled={isSaving || !allQuestionsAnswered}
									onClick={() => {
										setErrorBuildingAcknowledgementText(false);
										setAcknowledgementText("");

										const answers = buildSaveModel();
										buildAcknowledgementTextHandler({
											saveRequest: answers,
											testProcessHooks: testHooksOnTextBuild || false,
										});
										setShowAcknowledgement(true);
									}}
								>
									Finish Editing Questionnaire
								</Button>
							)}
							{footerButtons}
							{cancelButton && onCancel && (
								<Button type="button" onClick={onCancel}>
									Cancel
								</Button>
							)}
						</ButtonGroup>
					</ModalFooter>
				</>
			</ModalContent>
		</Modal>
	);
});
